import {
  StudioAnimation,
  StudioCell,
  StudioScene,
  StudioTextAnimationStep,
} from '..';
import {
  StudioAnimationMetaData,
  LayoutAnimationFamily,
  LayoutAnimationStep,
} from '../animation/animation.types';
import {
  StudioTextShape,
  StudioTextShapeDescriptor,
} from '../text/studio-text-shape';

import {
  LAYOUT_ANIMATION_OPTIONS,
  TEXT_ANIMATION_OPTIONS,
} from '../animation/text/studio-text-animation-presets';

import { v4 as uuidv4 } from 'uuid';

export interface AnimationOptionItem {
  label: string;
  key: string;
  isActive: boolean;
  options?: any[];
}

export interface AnimationOptions {
  mode: 'layout' | 'text' | null;
  items: AnimationOptionItem[];
}

export interface LayoutCellDefinition extends StudioCell {
  shape?: string;
  metaKey?: 'row' | 'column' | 'card-row' | 'card-column' | 'grid';
  id?: string;
  type?: 'row' | 'column' | 'grid';
  animationMetaData?: {
    appear?: StudioAnimationMetaData;
    action?: StudioAnimationMetaData;
    disappear?: StudioAnimationMetaData;
  };

  alignment?: 'start' | 'center' | 'end';
  contentAlignment?: 'start' | 'center' | 'end';
  justifyContent?:
    | 'start'
    | 'center'
    | 'end'
    | 'space-between'
    | 'space-around';
  gap?: number;
  children?: LayoutCellDefinition[];
  animation?: {
    appear?: StudioAnimation;
    action?: StudioAnimation;
    disappear?: StudioAnimation;
  };
}

export class LayoutCell {
  get id() {
    return this.definition.id;
  }

  set id(value: string) {
    this.definition.id = value;
  }
  children: LayoutCell[] = [];
  absoluteDims: { w: number; h: number; x: number; y: number };

  get label(): string {
    return this.definition.shape || this.id || 'cell';
  }

  get depth(): number {
    let d = 0;
    let p = this.parent;
    while (p) {
      d++;
      p = p.parent;
    }
    return d;
  }

  get hasChildren(): boolean {
    return this.children.length > 0;
  }

  get childCount(): number {
    return this.children.length;
  }

  get type(): string | undefined {
    return this.definition.type;
  }

  get contentType(): 'text' | 'image' | 'video' | undefined {
    if (this.hasChildren) return undefined;
    const shape = this.definition.shape;
    if (!shape) return undefined;
    if (this.scene.texts?.includes(shape)) return 'text';
    if (this.scene.images?.includes(shape)) return 'image';
    if (this.scene.videoInstances?.includes(shape)) return 'video';
    return undefined;
  }

  getAnimationOptions(
    type: 'appear' | 'disappear' | 'action',
  ): AnimationOptions {
    const activeMeta = this.definition.animationMetaData?.[type];

    if (this.hasChildren && this.childCount > 1) {
      return {
        mode: 'layout',
        items: LAYOUT_ANIMATION_OPTIONS.map(o => ({
          label: o.label,
          key: o.key,
          isActive: o.key === activeMeta?.family,
        })),
      };
    }

    if (this.contentType === 'text') {
      return {
        mode: 'text',
        items: TEXT_ANIMATION_OPTIONS.map(o => ({
          label: o.label,
          key: o.key,
          isActive: o.key === activeMeta?.family,
          options: o.options,
        })),
      };
    }

    return { mode: null, items: [] };
  }

  setSelectedAnimationMetaData(
    type: 'appear' | 'disappear' | 'action',
    family: string,
    variant?: string,
    option?: string,
  ): void {
    if (!this.definition.animationMetaData) {
      this.definition.animationMetaData = {};
    }
    this.definition.animationMetaData[type] = { family, variant, option };
  }

  // ── Build animation descriptor from metadata ───────────────────────────────

  private static readonly TRANSLATE_FROM: Record<string, [number, number]> = {
    left: [-80, 0],
    right: [80, 0],
    top: [0, -40],
    bottom: [0, 40],
  };

  get studioAnimation() {
    const appear = this.definition.animationMetaData?.appear;
    if (!appear) return null;
    const { family, variant, option } = appear;
    return this.buildAppearAnimation(family, variant, option);
  }

  buildAppearAnimation(
    family: string,
    variant?: string,
    option?: string,
  ): StudioTextAnimationStep | null {
    const target = this.definition.shape;
    if (!target) return null;

    const step: StudioTextAnimationStep = {
      target,
      type: 'appear',
      duration: 1.25,
    };

    this.definition.animationMetaData = { appear: { family, variant, option } };

    if (family === 'appear:line:translate') {
      const dir = LayoutCell.TRANSLATE_FROM[variant];
      if (dir) {
        step.translate = { from: dir };
      }
      if (option === 'horizontal-mask' || option === 'vertical-mask-appear') {
        step.translate = { ...(step.translate || {}), mask: true } as any;
      }
      if (option === 'word') {
        step.word = { overlap: 0.4 };
      }
      if (option === 'gradient-mask') {
        step.gradientMask = 'left';
      }
      if (option === 'word+translate') {
        step.word = { overlap: 0.4, translate: { from: dir || [0, 40] } };
      }
    } else if (family === 'appear:line:zoom') {
      const isIn = variant === 'in';
      step.zoom = { from: isIn ? 0.6 : 1.4, to: 1 };
      if (option === 'h-mask' || option === 'v-mask') {
        step.translate = { mask: true } as any;
      }
      if (option === 'word') {
        step.word = { overlap: 0.4 };
      }
    } else if (family === 'appear:word') {
      step.word = { overlap: 0.4 };
      if (variant?.includes('continuous')) {
        step.line = { align: 'continuous' };
      } else if (variant?.includes('discrete')) {
        step.line = { align: 'discrete' };
      }
      if (option === 'zoom') {
        step.word.zoom = [0.6, 1];
      }
      if (option === 'left') {
        step.word.translate = { from: [-40, 0] };
      }
      if (option === 'blur') {
        step.blur = { from: 8, to: 0, duration: 0.6 };
      }
      if (option === 'letter') {
        step.letter = { overlap: 0.85 };
      }
    } else if (family === 'appear:letter') {
      step.letter = { overlap: 0.85 };
      if (variant?.includes('continuous')) {
        step.line = { align: 'continuous' };
      }
      if (option === 'move') {
        step.translate = { from: [0, 20] };
      }
      if (option === 'zoom') {
        step.zoom = { from: 0.6, to: 1 };
      }
      if (option === 'blur') {
        step.blur = { from: 8, to: 0, duration: 0.6 };
      }
      if (option === 'cursor') {
        (step.letter as any).cursor = true;
      }
    } else {
      return null;
    }

    return step;
  }

  buildIsolatedPreviewScene(animation?: StudioAnimation): StudioScene | null {
    const shapeId = this.definition.shape;
    if (!shapeId) return null;

    const textShape = this.scene.shapes?.[shapeId];
    if (!textShape) return null;

    const isolatedLayout: LayoutCellDefinition = {
      w: 1,
      h: 1,
      padding: 0.2,
      type: 'column',
      children: [{ shape: shapeId }],
    };

    return {
      ...this.scene,
      texts: [shapeId],
      images: [],
      videoInstances: [],
      elements: [],
      layoutDefinition: isolatedLayout,
      shapes: { [shapeId]: { ...textShape } },
      animation: animation || undefined,
    };
  }

  constructor(
    public readonly scene: StudioScene,
    public readonly parent: LayoutCell,
    public readonly definition: LayoutCellDefinition,
    protected readonly dims?: { W: number; H: number },
    protected readonly registry?: Record<string, LayoutCell>,
  ) {
    this.id = definition.id || uuidv4();

    if (registry) {
      registry[this.id] = this;
      if (definition.shape) {
        registry[definition.shape] = this;
      }
    }

    this.children = (definition?.children || []).map(
      child => new LayoutCell(this.scene, this, child, dims, registry),
    );
  }
  getCellPosition(
    n: number,
    targetIndex: number,
  ): { x: number; y: number; w: number; h: number } {
    const { x, y, w, h } = this.absoluteDims;
    const { padding } = this.definition;
    const gap = this.definition.gap || 0;
    const numberOfChildren = this.children.length;
    if (this.definition.type === 'column') {
      const py = Array.isArray(padding) ? padding[1] : padding || 0;
      const netHeight = h - 2 * py;
      const itemH =
        (netHeight - gap * (numberOfChildren - 1)) / numberOfChildren;

      const nItemHeight = itemH * n + gap * (n - 1);

      const cellOffset = y + py;
      const offsetY = (netHeight - nItemHeight) / 2;

      return {
        x,
        y: cellOffset + offsetY + targetIndex * (itemH + gap),
        w,
        h: itemH,
      };
    }

    if (this.definition.type === 'row') {
      const px = Array.isArray(padding) ? padding[0] : padding || 0;
      const netWidth = w - 2 * px;
      const itemW =
        (netWidth - gap * (numberOfChildren - 1)) / numberOfChildren;

      const nItemWidth = itemW * n + gap * (n - 1);

      const cellOffset = x + px;
      const offsetX = (netWidth - nItemWidth) / 2;

      return {
        x: cellOffset + offsetX + targetIndex * (itemW + gap),
        y,
        w: itemW,
        h,
      };
    }
  }

  getAnimation(): StudioAnimation {
    let animation: StudioAnimation =
      this.id && this.id !== 'root'
        ? {
            target: this.id,
          }
        : undefined;

    switch (this.children.length) {
      case 0:
        return {
          type: 'appear',
          duration: 2,
          target: this.id,
          overlap: 0.5,
        };
      case 1:
        const childAnimation = this.children[0].getAnimation();
        if (!animation) {
          return childAnimation;
        }
        animation = {
          type: 'appear',
          duration: 2,
          target: this.id,
          next: {
            ...childAnimation,
            delay: 1.5,
          },
        };
        break;
      default:
        const children = this.children;
        const animations = children.map(child => child.getAnimation());
        animations.reverse();
        // -- // -- // -- // -- //
        // -- // -- //
        const childrenAnimations = animations.reduce((acc, animation, i) => {
          if (!acc) {
            return animation;
          }
          acc.delay = 0.3;
          animation.paralell = acc;
          return animation;
        }, {} as StudioAnimation);

        if (!animation) {
          return childrenAnimations;
        }
        animation = {
          type: 'appear',
          duration: 1,
          target: this.id,
          overlap: 0.5,
          next: {
            ...childrenAnimations,
            delay: 1.5,
          },
        };
        break;
    }
    return animation;
  }

  // ── Layout animation step generation ────────────────────────────────────────

  getLayoutAnimationSteps(
    family: LayoutAnimationFamily,
  ): LayoutAnimationStep[] {
    const elements = this.children
      .map(c => c.definition.shape || c.id)
      .filter(Boolean);
    switch (family) {
      case 'fixed':
        return this._getFixedSteps(elements);
      case 'replace':
        return this._getReplaceSteps(elements);
      case 'shift':
        return this._getShiftSteps(elements);
      case 'carousel':
        return this._getCarouselSteps(elements);
      default:
        return [];
    }
  }

  private _getFixedSteps(elements: string[]): LayoutAnimationStep[] {
    return elements.map(target => ({
      type: 'appear' as const,
      target,
    }));
  }

  private _getReplaceSteps(elements: string[]): LayoutAnimationStep[] {
    const steps: LayoutAnimationStep[] = [];
    for (let i = 0; i < elements.length; i++) {
      // const shape = this.scene.shapes[elements[i]]; //
      steps.push({
        type: 'appear',
        target: elements[i],
        position: this.getCellPosition(1, 0),
      });
      if (i < elements.length - 1) {
        steps.push({ type: 'disappear', target: elements[i] });
      }
    }
    return steps;
  }

  private _getShiftSteps(elements: string[]): LayoutAnimationStep[] {
    const steps: LayoutAnimationStep[] = [];

    steps.push({
      type: 'appear',
      target: elements[0],
      position: this.getCellPosition(1, 0),
    });

    for (let i = 1; i < elements.length; i++) {
      const n = i + 1;
      for (let j = 0; j < i; j++) {
        const step: LayoutAnimationStep = {
          type: 'move',
          target: elements[j],
          position: this.getCellPosition(n, j),
        };
        if (j > 0) step.parallel = true;
        steps.push(step);
      }
      steps.push({
        type: 'appear',
        target: elements[i],
        position: this.getCellPosition(n, i),
        parallel: true,
      });
    }

    return steps;
  }

  private _getCarouselSteps(elements: string[]): LayoutAnimationStep[] {
    const slot = (i: number) => this.getCellPosition(3, i);
    const steps: LayoutAnimationStep[] = [];

    steps.push({
      type: 'appear',
      target: elements[0],
      position: slot(1),
      effect: { zoom: 1.0, opacity: 1.0 },
    });
    steps.push({
      type: 'appear',
      target: elements[1],
      position: slot(2),
      effect: { zoom: 0.8, opacity: 0.5 },
      parallel: true,
    });

    for (let i = 1; i < elements.length; i++) {
      const centerElem = elements[i - 1];
      const currElem = elements[i];
      const nextElem = elements[i + 1];

      if (i >= 2) {
        steps.push({ type: 'disappear', target: elements[i - 2] });
        steps.push({
          type: 'move',
          target: centerElem,
          position: slot(0),
          effect: { zoom: 0.8, opacity: 0.5 },
          parallel: true,
        });
      } else {
        steps.push({
          type: 'move',
          target: centerElem,
          position: slot(0),
          effect: { zoom: 0.8, opacity: 0.5 },
        });
      }

      steps.push({
        type: 'move',
        target: currElem,
        position: slot(1),
        effect: { zoom: 1.0, opacity: 1.0 },
        parallel: true,
      });

      if (nextElem) {
        steps.push({
          type: 'appear',
          target: nextElem,
          position: slot(2),
          effect: { zoom: 0.8, opacity: 0.5 },
          overlap: 0.3,
        });
      }
    }

    return steps;
  }

  get shapeCells() {
    return {
      [this.id]: {
        ...this.absoluteDims,
        padding: this.definition.padding,
        background: this.definition.background,
        fontSize: this.definition.fontSize,
      },
      ...this.children.reduce(
        (object, child) => ({
          ...object,
          ...child.shapeCells,
        }),
        {},
      ),
    };
  }

  calChildrenDimns() {
    const { w, h, x, y } = this.absoluteDims;

    const { gap, padding, type } = this.definition;

    const _gap = gap || 0;
    const xGap = _gap;
    const yGap = _gap;

    let px = 0;
    let py = 0;

    if (padding) {
      if (Array.isArray(padding)) {
        [px, py] = padding;
      } else {
        px = padding || 0;
        py = padding || 0;
      }
    }

    const _px = px;
    const _py = py;
    // -- //
    let _x = x + _px;
    let _y = y + _py;

    const absWidth = w - 2 * _px;
    const absHeight = h - 2 * _py;

    const gapCnt = this.children.length - 1;
    let itemWidth = 0;
    let itemHeight = 0;
    switch (type) {
      case 'row':
        itemWidth = (absWidth - gapCnt * xGap) / this.children.length;
        itemHeight = absHeight;
        // -- //
        this.children.map(child => {
          const { definition } = child;
          const { w: childWidthDef, h: childHeightDef } = definition;
          //const childWidth =
          //  childWidthDef !== undefined ? childWidthDef * absWidth : itemWidth;

          // We need to align the elements vertically
          const _h = (childHeightDef || 1) * itemHeight;
          const alignOffset = (itemHeight - _h) / 2;
          // console.log('alignOffset', alignOffset);
          child.absoluteDims = {
            x: _x,
            y: _y + alignOffset,
            w: itemWidth,
            h: _h,
          };
          _x += itemWidth + xGap;
        });
        break;
      case 'column':
        itemWidth = absWidth;
        itemHeight = (absHeight - gapCnt * yGap) / this.children.length;
        this.children.map(child => {
          // const childHeight =
          //   child.definition.h !== undefined
          //     ? child.definition.h * absHeight
          //     : itemHeight;
          child.absoluteDims = {
            x: _x,
            y: _y,
            w: itemWidth,
            h: itemHeight,
          };
          _y += itemHeight + yGap;
        });
        break;
      case 'grid':
        break;
    }

    // Multiple text child
    if (this.children.length > 1) {
      const textChildren = this.children.filter(child => {
        return this.scene.texts.includes(child.definition.shape);
      });

      if (textChildren.length > 1) {
        const fontSize = Math.min(
          ...textChildren.map(child => {
            const shape = this.scene.shapes[child.definition.shape];
            return StudioTextShape.getStaticFontSize(
              child.absoluteDims.w * (this.dims?.W || 1),
              child.absoluteDims.h * (this.dims?.H || 1),
              (shape as StudioTextShapeDescriptor).text,
              'Montserrat',
            );
          }),
        );

        textChildren.forEach(child => {
          child.definition.fontSize = fontSize;
        });
      }
    }

    this.children.forEach(child => child.calChildrenDimns());
  }
}
export class RootLayoutCell extends LayoutCell {
  get id() {
    return 'root';
  }

  set id(_value: string) {
    // Root always keeps id 'root'
  }

  get completeBaseCell() {
    const { x, y, w, h } = this.absoluteDims;
    const { W, H } = this.dims || { W: 1, H: 1 };
    return {
      x: x * W,
      y: y * H,
      w: w * W,
      h: h * H,
    };
  }

  constructor(
    public readonly scene: StudioScene,
    definition: LayoutCellDefinition,
    dims?: { W: number; H: number },
  ) {
    const registry: Record<string, LayoutCell> = {};
    super(scene, null, definition, dims, registry);
    this.cellsById = registry;
    let { w, h } = definition;

    w ||= 1;
    h ||= 1;
    let x: number, y: number;
    switch (this.definition.alignment) {
      case 'start':
        x = 0;
        y = 0;
        break;
      case 'end':
        x = 0;
        y = 1 - h;
        break;
      default:
      case 'center':
        x = (1 - w) / 2;
        y = (1 - h) / 2;
        break;
    }

    this.absoluteDims = {
      x,
      y,
      w,
      h,
    };
    this.calChildrenDimns();
  }

  // getAnimation() {
  //   if (this.children.length == 1) {
  //     return this.children[0].getAnimation();
  //   }
  //   // -- // -- // -- //

  //   // const childrenTimelines = this.children.map(child => child.getAnimation());
  //   // childrenTimelines.forEach(child => {
  //   //   timeline.add(child);
  //   // });
  // }

  // ── Cell registry ───────────────────────────────────────────────────────────

  cellsById: Record<string, LayoutCell> = {};

  selectedCell: LayoutCell | null = null;

  selectCell(id: string): LayoutCell | null {
    const cell = this.cellsById[id];
    if (!cell) return null;

    // Skip single-child wrappers
    if (cell.hasChildren && cell.childCount === 1) {
      return this.selectCell(cell.children[0].id);
    }

    this.selectedCell = cell;
    return cell;
  }

  /**
   * Compose the cell tree into a thin StudioAnimation chain.
   * Each node only carries `target` and `type` — the renderer resolves
   * full animation details via `cellsByShapeId[target].studioAnimation`.
   *
   * If the scene has `background.animation`, a `__background__` move node
   * is prepended to the chain automatically.
   */
  getStudioAnimation(): StudioAnimation | null {
    const family = this.definition.animationMetaData?.appear?.family || 'fixed';
    const targets = this.children
      .map(c => c.definition.shape || c.id)
      .filter(Boolean);

    let chain: StudioAnimation | null = null;
    if (targets.length) {
      switch (family) {
        case 'replace':
          chain = this.chainReplace(targets);
          break;
        case 'fixed':
        default:
          chain = this.chainSequential(targets);
          break;
      }
    }

    if (this.scene.background?.animation) {
      const bgNode: StudioAnimation = {
        target: '__background__',
        type: 'move',
      };
      if (chain) {
        bgNode.paralell = chain;
      }
      return bgNode;
    }

    return chain;
  }

  private chainSequential(targets: string[]): StudioAnimation {
    const nodes: StudioAnimation[] = targets.map(target => ({
      target,
      type: 'appear',
    }));
    for (let i = 0; i < nodes.length - 1; i++) {
      nodes[i].next = nodes[i + 1];
    }
    return nodes[0];
  }

  private chainReplace(targets: string[]): StudioAnimation {
    const nodes: StudioAnimation[] = [];
    for (let i = 0; i < targets.length; i++) {
      nodes.push({ target: targets[i], type: 'appear' });
      if (i < targets.length - 1) {
        nodes.push({ target: targets[i], type: 'disappear' });
      }
    }
    for (let i = 0; i < nodes.length - 1; i++) {
      nodes[i].next = nodes[i + 1];
    }
    return nodes[0];
  }

  get cellsByShapeId(): Record<string, LayoutCell> {
    const result: Record<string, LayoutCell> = {};
    this.collectCellsByShapeId(this, result);
    return result;
  }

  private collectCellsByShapeId(
    cell: LayoutCell,
    result: Record<string, LayoutCell>,
  ): void {
    if (cell.definition.shape) {
      result[cell.definition.shape] = cell;
    }
    for (const child of cell.children) {
      this.collectCellsByShapeId(child, result);
    }
  }
}
